import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  limit,
  getDoc,
  orderBy,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import Modal from "react-bootstrap/Modal";
import { useAlert } from "react-alert";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import emailjs from "../../../utility/emailjsWithFallback";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/nl";
import getOrders from "../../../utility/getOrders";
import downloadReceipt from "../../../utility/createReceiptPDF";
import "./History.scss";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/nl";

import { db } from "../../../firebase";

import itemSoldImg from "../../../images/itemSold.svg";
import totalRevenueImg from "../../../images/totalRevenue.svg";

import { ReactComponent as All } from "../../../images/All.svg";
import { ReactComponent as Card } from "../../../images/Card.svg";
import { ReactComponent as Cash } from "../../../images/Cash.svg";
import { ReactComponent as Qr } from "../../../images/Qr.svg";
import { ReactComponent as Refund } from "../../../images/Refund.svg";
import { ReactComponent as Free } from "../../../images/gift-card.svg";
import { ReactComponent as Invoice } from "../../../images/invoice.svg";
import { ReactComponent as PendingNote } from "../../../images/payment.svg";
import { ReactComponent as Cashless } from "../../../images/nfc-card.svg";
import { ReactComponent as Split } from "../../../images/Split.svg";

import { Icons } from "../../../images/icons";
import CategoryFilter from "../../../components/CategoryFilter/CategoryFilter";
import ENV from "../../../env";
import ChartCard from "../../../components/ChartCard/ChartCard";
import Skeleton from "../../../components/skeletonLoader/SkeletonLoader";

let now = new Date();
let initialStartDate = new Date(now);
let initialEndDate = new Date(now);
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

initialStartDate.setDate(initialStartDate.getDate() - 1);
initialEndDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
initialStartDate.setHours(0, 0, 0, 0);

const isInvoicePaid = (invoice) =>
  Boolean(invoice?.paid || invoice?.checkoutPaymentStatus === "paid" || invoice?.status === "paid");

const isCountableOrder = (order) =>
  !order?.status || ["paid", "complete", "completed"].includes(String(order.status).toLowerCase());

const isFailedCardOrder = (order) =>
  order?.paymentType === "card" && Boolean(order?.status) && !isCountableOrder(order);

const getOrganizationName = (organization) =>
  organization?.organization || organization?.legalName || organization?.id || "-";

const matchesOrderFilter = (order, filter) => {
  if (filter === "All") return true;
  if (filter === "Card") return order?.paymentType === "card";
  if (filter === "FailedCard") return isFailedCardOrder(order);
  if (filter === "Cash") return order?.paymentType === "cash";
  if (filter === "Qr") return order?.paymentType === "qr";
  if (filter === "Refund") return order?.totalAmount < 0;
  if (filter === "Free") return order?.paymentType === "free";
  if (filter === "Cashless") return order?.paymentType === "cashless";
  if (filter === "TableNote") return order?.paymentType === "tableNote";
  if (filter === "BankTransfer") return order?.paymentType === "bankTransfer";
  if (filter === "SplitPayments") return order?.paymentType === "splitPayments";
  return false;
};

const STRIPE_EXPORT_EXCLUDED_KEYS = new Set([
  "stripeAccountId",
  "stripeSessionId",
  "stripeCustomerId",
  "stripeCustomerEmail",
  "stripeInvoiceId",
  "checkoutUrl",
  "hostedInvoiceUrl",
  "checkoutSessionStatus",
  "checkoutPaymentStatus",
  "paymentIntentId",
  "sessionExpiresAt",
  "applicationFeeCents",
  "products",
  "invoicingFeeCents" 
]);

const shouldExcludeInvoiceExportKey = (key) =>
  STRIPE_EXPORT_EXCLUDED_KEYS.has(key) || key.toLowerCase().startsWith("stripe");

const exportToCsv = async (startDate, endDate, organizationIds, organizations) => {
  const selectedOrganizationIds = [...new Set(organizationIds.filter(Boolean))];
  const isMultiOrganizationSearch = selectedOrganizationIds.length > 1;
  const organizationNames = new Map(
    organizations.map((organization) => [organization.id, getOrganizationName(organization)]),
  );
  const orderData = await getOrders({
    orgId: selectedOrganizationIds[0],
    orgIds: selectedOrganizationIds,
    startTimestamp: startDate.getTime().toString(),
    endTimestamp: endDate.getTime().toString(),
    orderDirection: "desc",
    limit: 5000,
  });

  const productColumns = {};

  orderData.forEach((order) => {
    order.products.forEach((product) => {
      const quantity = Number(product.quantity) || 0;
      if (!productColumns[product.title]) {
        productColumns[product.title] = 0;
      }
      productColumns[product.title] += quantity;
    });
  });

  const formattedData = orderData.map((order) => {
    const invoiceNumber = order?.invoice?.invoiceNumber || order?.invoiceNumber || null;
    const row = {
      id: order.id,
      payment_type: order.paymentType,
      status: order.status || "confirmed",
      timestamp: order.createdAt
        ? moment(parseInt(order.createdAt)).format("DD MMMM YYYY HH:mm:ss")
        : "-",
      cashier: order.createdBy === "cashless" ? "Fast-Pass" : order.creatorName,
      customer: order?.customerEmail
        ? order?.customerEmail
        : order.createdBy === "cashless"
          ? order.creatorName
          : "-",
      table: order?.tableNumber ? `Table ${order.tableNumber} ${order.tableName}` : "-",
      amount: parseFloat(order.totalAmount),
      invoice_number: invoiceNumber || "-",
    };

    if (isMultiOrganizationSearch) {
      row.POS = organizationNames.get(order.orgId) || order.orgId || "-";
    }

    // Initialize product quantities to 0
    Object.keys(productColumns).forEach((productTitle) => {
      row[productTitle] = 0;
    });

    order.products.forEach((product) => {
      const quantity = Number(product.quantity) || 0;
      row[product.title] = (row[product.title] || 0) + quantity; // Add product quantity to the row
    });

    return row;
  });

  const headers = [
    "id",
    "payment_type",
    "status",
    ...(isMultiOrganizationSearch ? ["POS"] : []),
    "amount",
    "timestamp",
    "cashier",
    "customer",
    "table",
    "invoice_number",
    ...Object.keys(productColumns),
  ];

  var startDate1 = startDate.getTime();
  var endDate1 = endDate.getTime();

  var start = new Date(startDate1);
  start.setHours(start.getHours());

  var end = new Date(endDate1);
  end.setHours(end.getHours() + 24);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formattedStartDate = formatDate(start);
  const formattedEndDate = formatDate(end);

  const worksheet = XLSX.utils.json_to_sheet(formattedData, {
    header: headers,
  });
  const csv = XLSX.utils.sheet_to_csv(worksheet, { FS: ";" });
  const csvBlob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const csvUrl = URL.createObjectURL(csvBlob);
  const downloadLink = document.createElement("a");

  downloadLink.href = csvUrl;
  downloadLink.download = `Orders_${formattedStartDate}_${formattedEndDate}.csv`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(csvUrl);
};

function History({ organizations, orgId, paymentMethod }) {
  const alert = useAlert();
  const { t, i18n } = useTranslation("history");

  moment.locale(i18n.language);
  dayjs.locale(i18n.language);

  const [orderData, setOrderData] = useState([]);
  const [itemSold, setItemSold] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loader, setLoader] = useState(false);
  const [exportOrdersLoader, setExportOrdersLoader] = useState(false);
  const [exportInvoicesLoader, setExportInvoicesLoader] = useState(false);
  const [orderSelected, setOrderSelected] = useState({});
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [members, setMembers] = useState([]);
  const [selectedCashier, setSelectedCashier] = useState();
  const [linearGraphValues, setLinearGraphValues] = useState([]);
  const [isResendingInvoiceEmail, setIsResendingInvoiceEmail] = useState(false);
  const [isMarkingOrderPaid, setIsMarkingOrderPaid] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [includeCashlessRevenue, setIncludeCashlessRevenue] = useState(false);
  const [includedOrgIds, setIncludedOrgIds] = useState([]);
  const [showOtherPosModal, setShowOtherPosModal] = useState(false);
  const [pendingIncludedOrgIds, setPendingIncludedOrgIds] = useState([]);
  const [hasReachedOrderLimit, setHasReachedOrderLimit] = useState(false);

  const [filter, setFilter] = useState("All");

  const [show, setShow] = useState(false);
  const [reloading, setReloading] = useState(false);

  const primaryOrgId = orgId?.[0];
  const selectedOrg = organizations?.find((org) => org.id === primaryOrgId);
  const selectedOrganizationIds = [
    primaryOrgId,
    ...includedOrgIds.filter((id) => id !== primaryOrgId && organizations.some((org) => org.id === id)),
  ].filter(Boolean);
  const selectedOrganizations = organizations.filter((org) => selectedOrganizationIds.includes(org.id));
  const otherOrganizations = organizations.filter((org) => org.id !== primaryOrgId);
  const isMultiOrganizationSearch = selectedOrganizationIds.length > 1;

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const openOtherPosModal = () => {
    setPendingIncludedOrgIds(includedOrgIds);
    setShowOtherPosModal(true);
  };

  async function getMembersFromDb(foundOrganizations) {
    const organizationsToLoad = Array.isArray(foundOrganizations)
      ? foundOrganizations.filter(Boolean)
      : [foundOrganizations].filter(Boolean);

    if (!organizationsToLoad.length) {
      alert.show(t("alert.noOrgSelected"), { type: "error" });
      return;
    }

    const rles = ["adminUid", "cashierUid", "stewardUid"];
    const roleLabels = {
      adminUid: "admin",
      cashierUid: "cashier",
      stewardUid: "steward",
    };

    try {
      const memberRoles = new Map();
      organizationsToLoad.forEach((organization) => {
        rles.forEach((role) => {
          // Older organisation records can omit a role or store a single UID
          // instead of an array. Normalise both shapes before iterating.
          const roleMembers = Array.isArray(organization[role])
            ? organization[role]
            : typeof organization[role] === "string" && organization[role]
              ? [organization[role]]
              : [];
          roleMembers.forEach((uid) => {
            if (typeof uid === "string" && uid.trim() && !memberRoles.has(uid)) {
              memberRoles.set(uid, roleLabels[role]);
            }
          });
        });
      });

      const memberResults = await Promise.all(
        [...memberRoles.entries()].map(async ([uid, roleInOrg]) => {
          const userDoc = await getDoc(doc(db, "Store", uid));
          return userDoc.exists()
            ? { ...userDoc.data(), uid: userDoc.data()?.uid || uid, roleInOrg }
            : null;
        }),
      );
      setMembers(memberResults.filter(Boolean));
    } catch (error) {
      console.error(t("error.failedFetchMembers"), error);
      alert.show(t("error.failedLoadMembers"), { type: "error" });
    }
  }

  const getOrdersData = async (organizationIds) => {
    setLoader(true);
    let records = [];
    const requestedOrganizationIds = Array.isArray(organizationIds)
      ? organizationIds
      : selectedOrganizationIds;
    const normalizedOrganizationIds = [...new Set(requestedOrganizationIds.filter(Boolean))];
    const organizationNames = new Map(
      organizations.map((organization) => [organization.id, getOrganizationName(organization)]),
    );

    const queryData = {
      orgId: normalizedOrganizationIds[0],
      orgIds: normalizedOrganizationIds,
      orderDirection: "desc",
      limit: 5000,
    };

    if (startDate && endDate && startDate < endDate) {
      queryData.startTimestamp = startDate.getTime().toString();
      queryData.endTimestamp = endDate.getTime().toString();
    }

    if (selectedCashier) {
      queryData.createdBy = selectedCashier;
    }

    try {
      const ordersResult = await getOrders({ ...queryData, withMetadata: true });
      records = ordersResult.orders;
      setHasReachedOrderLimit(ordersResult.limitReached);
      // Keep the history focused on completed sales, while also exposing card
      // attempts that require a cashier to validate the payment manually.
      records = records.filter((order) => isCountableOrder(order) || isFailedCardOrder(order));
      const invoiceIds = records.filter((order) => order.invoiceId).map((order) => order.invoiceId);
      const uniqueInvoiceIds = [...new Set(invoiceIds)];

      const invoicePromises = uniqueInvoiceIds.map(async (invoiceId) => {
        try {
          const invoiceDoc = await getDoc(doc(db, "Invoices", invoiceId));
          if (invoiceDoc.exists()) {
            return { id: invoiceId, data: invoiceDoc.data() };
          }
        } catch (error) {
          console.error(`Error fetching invoice ${invoiceId}:`, error);
        }
        return null;
      });

      const invoiceResults = await Promise.all(invoicePromises);
      const invoiceMap = {};
      invoiceResults.forEach((result) => {
        if (result) {
          invoiceMap[result.id] = result.data;
        }
      });

      records = records.map((order) => {
        const invoice = order.invoiceId ? invoiceMap[order.invoiceId] : null;
        return {
          ...order,
          invoice,
          invoiceNumber: order.invoiceNumber || invoice?.invoiceNumber || null,
          organizationName: organizationNames.get(order.orgId) || order.orgId || "-",
        };
      });
    } catch (error) {
      setHasReachedOrderLimit(false);
      console.log("Error fetching orders:", error);
      alert.show(
        t("error.failedLoadOrders", {
          defaultValue: "Impossible de charger les commandes. Veuillez réessayer.",
        }),
        { type: "error" },
      );
    }

    setOrderData(records);
    findPDetail(records);
    setLoader(false);
  };

  const findPDetail = (orderData1) => {
    let findQuantity = 0;
    let findRevenue = 0;

    orderData1.forEach((element) => {
      if (!isCountableOrder(element)) return;

      element.products.forEach((product) => {
        const quantity = Number(product.quantity) || 0;
        findQuantity += quantity;
      });

      const isTableNote = element.paymentType === "tableNote";
      const isCashless = element.paymentType === "cashless";

      if (!isTableNote && (includeCashlessRevenue || !isCashless)) {
        findRevenue += element.totalAmount;
      }
    });

    setTotalRevenue(findRevenue);
    setItemSold(findQuantity);
  };

  async function handleDeleteOrder(id) {
    setLoader(true);
    try {
      const orderRef = doc(db, "Orders", id);
      await deleteDoc(orderRef);
      await getOrdersData();
    } catch (error) {
      console.error("Error deleting order: ", error);
    }
    setLoader(false);
  }

  async function handleMarkOrderPaid() {
    if (!isFailedCardOrder(orderSelected) || isMarkingOrderPaid) return;

    setIsMarkingOrderPaid(true);
    try {
      await updateDoc(doc(db, "Orders", orderSelected.id), { status: "paid" });
      setOrderSelected((order) => ({ ...order, status: "paid" }));
      await getOrdersData();
      alert.show(t("order_marked_paid"), { type: "success" });
    } catch (error) {
      console.error("Error marking order as paid:", error);
      alert.show(t("order_mark_paid_error"), { type: "error" });
    } finally {
      setIsMarkingOrderPaid(false);
    }
  }

  useEffect(() => {
    if (primaryOrgId) {
      setSelectedCashier("");
      setIncludedOrgIds([]);
      setPendingIncludedOrgIds([]);
      setShowOtherPosModal(false);
      getOrdersData([primaryOrgId]);
    }
  }, [primaryOrgId, reloading]);

  useEffect(() => {
    if (selectedOrganizations.length) {
      getMembersFromDb(selectedOrganizations);
    }
  }, [organizations, primaryOrgId, includedOrgIds]);

  useEffect(() => {
    if (paymentMethod) {
      findPDetail(orderData);
    }
  }, [paymentMethod]);

  useEffect(() => {
    findPDetail(orderData);
  }, [includeCashlessRevenue]);

  useEffect(() => {
    groupOrders(orderData);
  }, [orderData]);

  const handleClickLiquidity = (item) => {
    setOrderSelected(item);
    handleShow();
  };

  const selectedInvoice = orderSelected?.invoice;
  const selectedInvoicePaid = isInvoicePaid(selectedInvoice);
  const selectedInvoiceIdForLink = orderSelected?.invoiceId || null;
  const selectedInvoicePdfUrl = selectedInvoiceIdForLink
    ? `/invoice/${selectedInvoiceIdForLink}`
    : selectedInvoice?.invoicePdf || selectedInvoice?.documentPath || null;

  const handleDownloadReceipt = async () => {
    if (!orderSelected?.id || isDownloadingReceipt) return;

    const orderIsPaid = isCountableOrder(orderSelected);
    const receipt = {
      ...orderSelected,
      ...selectedInvoice,
      id: selectedInvoice?.id || selectedInvoiceIdForLink || orderSelected.id,
      invoiceNumber:
        selectedInvoice?.invoiceNumber || orderSelected.invoiceNumber || orderSelected.id,
      products: selectedInvoice?.products || orderSelected.products || [],
      customerEmail: selectedInvoice?.customerEmail || orderSelected.customerEmail || "",
      paid: selectedInvoicePaid || orderIsPaid,
      checkoutPaymentStatus:
        selectedInvoice?.checkoutPaymentStatus || (orderIsPaid ? "paid" : undefined),
      paidAt: selectedInvoice?.paidAt || orderSelected.paidAt || orderSelected.createdAt,
      orderIds: selectedInvoice?.orderIds || [orderSelected.id],
      orderNumbers: selectedInvoice?.orderNumbers || orderSelected.id,
    };

    try {
      setIsDownloadingReceipt(true);
      await downloadReceipt({ invoice: receipt, organization: selectedOrg });
    } catch (error) {
      console.error("Failed to download order receipt:", error);
      alert.show(t("receipt_download_error"), { type: "error" });
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const handleResendInvoiceEmail = async () => {
    if (isResendingInvoiceEmail) {
      return;
    }

    const customerEmail = orderSelected?.customerEmail;

    if (!customerEmail) {
      alert.show(t("invoice_email_missing_email"), { type: "error" });
      return;
    }

    const invoiceId = orderSelected.invoiceId;

    if (!invoiceId) {
      alert.show(t("invoice_email_missing_link"), { type: "error" });
      return;
    }

    try {
      setIsResendingInvoiceEmail(true);

      const params = {
        orgMail: selectedOrg?.email || ENV?.emailJsToEmail,
        customerEmail,
        invoiceUrl: `https://ticket.comitty.be/invoice/${invoiceId}`,
        organization: selectedOrg?.legalName || selectedOrg?.organization || "",
      };

      await emailjs.send(
        ENV.emailJsServiceId,
        "template_meaehee",
        params,
        ENV?.emailJsPublicApiKey || "user_Io6hQDXLobAbA5jUwZICt",
      );

      alert.show(t("invoice_email_sent"), { type: "success" });
    } catch (error) {
      console.error("Error sending invoice email:", error);
      alert.show(t("invoice_email_error"), { type: "error" });
    } finally {
      setIsResendingInvoiceEmail(false);
    }
  };

  function groupOrders(orders) {
    const ordersByTimeSlot = {};
    const isADay = Math.abs(endDate - startDate) < ONE_DAY_MS;

    orders.forEach((order) => {
      const date = new Date(parseInt(order.createdAt));
      const dateKey = dayjs(date)
        .startOf(isADay ? "minute" : "hour")
        .toISOString();

      if (!ordersByTimeSlot[dateKey]) ordersByTimeSlot[dateKey] = 0;
      order.products.forEach((product) => (ordersByTimeSlot[dateKey] += product.quantity));
    });

    setLinearGraphValues(
      Object.keys(ordersByTimeSlot)
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => ({ date, value1: ordersByTimeSlot[date] })),
    );
  }

  const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

  const handleExport = async () => {
    try {
      setExportOrdersLoader(true);
      await exportToCsv(startDate, endDate, selectedOrganizationIds, organizations);
    } catch (error) {
      alert.show(t("export_error"), { type: "error" });
      console.error("Export error:", error);
    } finally {
      setExportOrdersLoader(false);
    }
  };

  const handleExportInvoices = async () => {
    if (!selectedOrg?.stripeActivated || !selectedOrg?.isTablePlanActive) {
      return;
    }

    const hasStartDate = isValidDate(startDate);
    const hasEndDate = isValidDate(endDate);

    if (hasStartDate && hasEndDate && startDate > endDate) {
      alert.show(t("invalid_date_range"), { type: "error" });
      return;
    }

    try {
      setExportInvoicesLoader(true);

      const constraints = [where("orgId", "==", selectedOrg.id)];

      if (hasStartDate) {
        constraints.push(where("createdAt", ">=", startDate.getTime()));
      }

      if (hasEndDate) {
        constraints.push(where("createdAt", "<=", endDate.getTime()));
      }

      constraints.push(orderBy("createdAt", "desc"));
      constraints.push(limit(2000));

      const invoicesSnapshot = await getDocs(query(collection(db, "Invoices"), ...constraints));

      if (invoicesSnapshot.empty) {
        alert.show(t("no_data"), { type: "info" });
        return;
      }

      const invoices = invoicesSnapshot.docs.map((invoiceDoc) => ({
        id: invoiceDoc.id,
        ...invoiceDoc.data(),
      }));

      const formattedInvoices = invoices.map((invoice) => {
        const { id, ...invoiceData } = invoice;

        const sanitizedData = Object.entries(invoiceData).reduce((acc, [key, value]) => {
          if (shouldExcludeInvoiceExportKey(key)) {
            return acc;
          }

          if (value === null || value === undefined) {
            acc[key] = "";
          } else if (Array.isArray(value)) {
            acc[key] = value.join(", ");
          } else if (typeof value === "object") {
            acc[key] = JSON.stringify(value);
          } else {
            acc[key] = value;
          }
          return acc;
        }, {});

        const createdAtFormatted = invoiceData?.createdAt
          ? moment(invoiceData.createdAt).format("DD MMMM YYYY HH:mm:ss")
          : "";
        const updatedAtFormatted = invoiceData?.updatedAt
          ? moment(invoiceData.updatedAt).format("DD MMMM YYYY HH:mm:ss")
          : "";

        return {
          id,
          ...sanitizedData,
          createdAtFormatted,
          updatedAtFormatted,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedInvoices);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");

      const getDateForFilename = (date, fallback) => {
        if (!isValidDate(date)) {
          return fallback;
        }
        return new Date(date.getTime());
      };

      const now = new Date();
      const startForName = getDateForFilename(startDate, now);
      const endForName = getDateForFilename(endDate, now);
      const endForNameAdjusted = new Date(endForName.getTime());
      endForNameAdjusted.setHours(endForNameAdjusted.getHours() + 24);

      const formatDateForFilename = (date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const formattedStart = formatDateForFilename(startForName);
      const formattedEnd = formatDateForFilename(endForNameAdjusted);

      XLSX.writeFile(workbook, `Invoices_${formattedStart}_${formattedEnd}.csv`, {
        bookType: "csv",
      });
    } catch (error) {
      console.error("Error exporting invoices:", error);
      alert.show(t("something_went_wrong"), { type: "error" });
    } finally {
      setExportInvoicesLoader(false);
    }
  };

  function AmountMainCard() {
    return (
      <div className="amountMainCard themeCard">
        <h5 className="subTitle">{t("filters")}</h5>
        <div className="text">{t("max_250_orders")}</div>

        <div className="pickerDivContainer">
          <div className="pickerDiv">
            <label className="white-text">{t("start_date")}</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="Pp"
              placeholderText={t("select_start_date")}
              className="form-control"
              popperClassName="datepicker-popper"
              calendarClassName="datepicker-calendar"
            />
          </div>
          <div className="pickerDiv">
            <label className="white-text">{t("end_date")}</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="Pp"
              placeholderText={t("select_end_date")}
              className="form-control"
              popperClassName="datepicker-popper"
              calendarClassName="datepicker-calendar"
            />
          </div>
        </div>

        <div className="pickerDiv">
          <label className="white-text">{t("select_cashier")}</label>
          <select
            value={selectedCashier}
            onChange={(e) => setSelectedCashier(e.target.value)}
            className="form-control"
          >
            <option value="">{t("choose_cashier")}</option>
            {members.map((cashier) => (
              <option key={cashier.uid} value={cashier.uid}>
                {cashier.username}
              </option>
            ))}
          </select>
        </div>
        {otherOrganizations.length > 0 && (
          <div className="pickerDiv history-multi-pos-picker">
            <label className="white-text">{t("include_other_pos")}</label>
            <div>
              <button
                type="button"
                className="history-multi-pos-trigger"
                onClick={openOtherPosModal}
              >
                {includedOrgIds.length
                  ? t("include_other_pos_selected", { count: includedOrgIds.length })
                  : t("include_other_pos_none")}
              </button>
            </div>
          </div>
        )}
        <div className="pickerDiv">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeCashlessRevenue"
              checked={includeCashlessRevenue}
              onChange={(e) => setIncludeCashlessRevenue(e.target.checked)}
            />
            <label className="form-check-label white-text" htmlFor="includeCashlessRevenue">
              {t("include_cashless_revenue")}
            </label>
          </div>
        </div>

        <button
          onClick={getOrdersData}
          className={`themeBtn ${hasReachedOrderLimit ? "mb-0" : ""}`}
        >
          {t("search")}
        </button>
        {hasReachedOrderLimit && <div className="alert">{t("more_than_250_orders")}</div>}
        <button
          className="themeBtn themeBtnSecondary exportOrdersBtn"
          type="button"
          onClick={handleExport}
          disabled={exportOrdersLoader}
        >
          {exportOrdersLoader ? (
            <span className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm text-white me-2" role="status" />
              <span>{t("please_wait")}</span>
            </span>
          ) : (
            t("export")
          )}
        </button>

        {selectedOrg?.stripeActivated && selectedOrg?.isTablePlanActive && (
          <button
            className="themeBtn themeBtnSecondary exportInvoicesBtn"
            type="button"
            onClick={handleExportInvoices}
            disabled={exportInvoicesLoader}
          >
            {exportInvoicesLoader ? (
              <span className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm text-white me-2" role="status" />
                <span>{t("please_wait")}</span>
              </span>
            ) : (
              t("export_invoices")
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="history">
      <div className="row mx-0">
        <div className="col-xl-8 col-xxl-9">
          <div className="col row1">
            <div className="w-full order-1 order-md-2">
              <div className="themeCardLineChart">
                <h3>{t("progress")}</h3>
                <ChartCard
                  data={linearGraphValues}
                  width={`max(100%, ${Math.max(500, linearGraphValues.length * 100)}px)`}
                  firstLineColor="#5A0CF6"
                  yAxisUnit=""
                  name1={t("products_sold")}
                  allowDecimals={false}
                  isLoading={loader}
                />
                <div className="total dn-lg ">
                  {t("total_revenue")}: €{" "}
                  {totalRevenue > 0 ? totalRevenue.toFixed(2) : totalRevenue}
                </div>
              </div>
            </div>
            <div className="totalsContainer">
              <div className={`flex-6 themeCard itemCard mb-0 ${loader ? "is-loading" : ""}`}>
                {loader ? (
                  <>
                    <Skeleton className="history-total-icon-skeleton" />
                    <div className="content">
                      <Skeleton className="history-total-value-skeleton" />
                      <Skeleton className="history-total-label-skeleton" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="itemImg">
                      <img className="img-fluid" src={itemSoldImg} alt="" />
                    </div>
                    <div className="content">
                      <h3>{itemSold}</h3>
                      <p>{t("products_sold")}</p>
                    </div>
                  </>
                )}
              </div>
              <div className={`flex-6 themeCard itemCard mb-0 ${loader ? "is-loading" : ""}`}>
                {loader ? (
                  <>
                    <Skeleton className="history-total-icon-skeleton" isLoader1 />
                    <div className="content">
                      <Skeleton className="history-total-value-skeleton" isLoader1 />
                      <Skeleton className="history-total-label-skeleton" isLoader1 />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="itemImg">
                      <img className="img-fluid" src={totalRevenueImg} alt="" />
                    </div>
                    <div className="content">
                      <h3>{totalRevenue > 0 ? totalRevenue.toFixed(2) : totalRevenue} €</h3>
                      <p>{t("total_revenue")}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="d-block d-xl-none mb-3">
            <AmountMainCard />
          </div>
          <Table
            orderData={orderData}
            setFilter={setFilter}
            filter={filter}
            startDate={startDate}
            endDate={endDate}
            handleClickLiquidity={(order) => {
              handleClickLiquidity(order);
            }}
            isMultiOrganizationSearch={isMultiOrganizationSearch}
          />
        </div>
        <div className="col-xl-4 col-xxl-3 d-none d-xl-block">
          <AmountMainCard />
        </div>
      </div>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{`${t("show_order")}: ${orderSelected?.id || "-"}`} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="orderDetails">
            {orderSelected?.id ? (
              <>
                <div className="mb-3 d-flex justify-content-between">
                  <label>{t("timestamp")}</label>
                  <div className="form-control-plaintext">
                    {orderSelected?.createdAt
                      ? moment(parseInt(orderSelected.createdAt)).format("DD MMMM YYYY HH:mm:ss")
                      : "-"}
                  </div>
                </div>
                <div className="mb-3 d-flex justify-content-between">
                  <label>{t("cashier")}</label>
                  <div className="form-control-plaintext">
                    {orderSelected.createdBy === "cashless"
                      ? "Fast-Pass"
                      : orderSelected.creatorName}
                  </div>
                </div>
                <div className="mb-3 d-flex justify-content-between">
                  <label>{t("customer")}</label>
                  <div className="form-control-plaintext">
                    {orderSelected?.customerEmail
                      ? orderSelected?.customerEmail
                      : orderSelected.createdBy === "cashless"
                        ? orderSelected.creatorName
                        : "-"}
                  </div>
                </div>
                <div className="mb-3 d-flex justify-content-between">
                  <label>{t("table")}</label>
                  <div className="form-control-plaintext">
                    {orderSelected?.tableNumber
                      ? `Table ${orderSelected.tableNumber} ${orderSelected.tableName}`
                      : "-"}
                  </div>
                </div>
                {orderSelected?.note && (
                  <div className="mb-3 d-flex justify-content-between">
                    <label>{t("comment")}</label>
                    <div className="form-control-plaintext">{orderSelected.note}</div>
                  </div>
                )}
                <div className="mb-3 d-flex justify-content-between">
                  <label>{t("payment_type")}</label>
                  <div className="form-control-plaintext">{t(orderSelected.paymentType)}</div>
                </div>
                {isFailedCardOrder(orderSelected) && (
                  <div className="mb-3 d-flex justify-content-between">
                    <label>{t("payment_status")}</label>
                    <div className="form-control-plaintext-pink">{t("card_payment_failed")}</div>
                  </div>
                )}
                {orderSelected?.invoice?.invoiceNumber && (
                  <div className="mb-3 d-flex justify-content-between">
                    <label>{t("invoice_number")}</label>
                    <div className="form-control-plaintext">
                      {orderSelected.invoice.invoiceNumber}
                    </div>
                  </div>
                )}
                {orderSelected.paymentType === "splitPayments" && (
                  <div>
                    <div className="mb-3">
                      <label>{t("split_payments_details")}</label>
                    </div>
                    {orderSelected.splitPayments?.map((sp, idx) => (
                      <div
                        key={idx}
                        className="mb-3 d-flex justify-content-between px-3"
                        style={{
                          backgroundColor: "#025e",
                          borderRadius: "5px",
                          padding: "10px",
                        }}
                      >
                        <div className="form-control-plaintext-left">{t(sp.paymentType)}</div>
                        <div className="form-control-plaintext">
                          {parseFloat(sp.amount).toFixed(2)} €
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mb-3 d-flex justify-content-between">
                  <div className="form-control-plaintext-left">{t("list")}</div>
                  <div className="form-control-plaintext">{t("quantity_price")}</div>
                </div>
                {orderSelected.products.map((product, index) => (
                  <div
                    key={index}
                    className="mb-2 mb-md-3 d-flex justify-content-between align-items-center px-3"
                    style={{
                      backgroundColor: "#025ef3",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                  >
                    <div className="form-control-plaintext-left">{product.title}</div>
                    <div className="form-control-plaintext">
                      {`${product.quantity} x ${product.price} €`}
                    </div>
                  </div>
                ))}
                <div className="d-flex justify-content-between">
                  <div className="form-control-plaintext-left">{t("total_amount")}</div>
                  <div
                    className={
                      orderSelected.totalAmount >= 0
                        ? "form-control-plaintext-green"
                        : "form-control-plaintext-pink"
                    }
                  >
                    {orderSelected.totalAmount.toFixed(2)}€
                  </div>
                </div>
                <button
                  type="button"
                  className="signupBtn mt-3"
                  onClick={handleDownloadReceipt}
                  disabled={isDownloadingReceipt}
                >
                  {isDownloadingReceipt ? t("receipt_downloading") : t("download_receipt")}
                </button>
                {orderSelected?.paymentType === "bankTransfer" && (
                  <div className="d-flex flex-column gap-2 mt-3">
                    {!selectedInvoicePaid && (
                      <button
                        type="button"
                        className="signupBtn"
                        onClick={handleResendInvoiceEmail}
                        disabled={isResendingInvoiceEmail}
                      >
                        {isResendingInvoiceEmail
                          ? t("invoice_email_sending")
                          : t("resend_invoice_email")}
                      </button>
                    )}
                    {selectedInvoicePdfUrl && (
                      <a
                        href={selectedInvoicePdfUrl}
                        className="signupBtn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("open_invoice")}
                      </a>
                    )}
                  </div>
                )}
                {isFailedCardOrder(orderSelected) && (
                  <button
                    type="button"
                    className="signupBtn mt-3"
                    onClick={handleMarkOrderPaid}
                    disabled={isMarkingOrderPaid}
                  >
                    {isMarkingOrderPaid ? t("marking_order_paid") : t("mark_order_paid")}
                  </button>
                )}
                {["cash", "free", "cashless"].includes(orderSelected?.paymentType) && (
                  <button
                    type="button"
                    className="signupBtn mt-3"
                    onClick={() => {
                      handleDeleteOrder(orderSelected.id);
                      handleClose();
                    }}
                  >
                    {t("delete_order")}
                  </button>
                )}
              </>
            ) : (
              <div>{t("no_order_selected")}</div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showOtherPosModal}
        onHide={() => setShowOtherPosModal(false)}
        centered
        dialogClassName="history-other-pos-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t("include_other_pos")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="history-other-pos-modal-subtitle">{t("include_other_pos_modal_subtitle")}</p>
          <div className="history-other-pos-modal-toolbar">
            <span>{t("include_other_pos_modal_count", { count: pendingIncludedOrgIds.length })}</span>
            <div>
              <button
                type="button"
                onClick={() => setPendingIncludedOrgIds(otherOrganizations.map((organization) => organization.id))}
              >
                {t("include_other_pos_select_all")}
              </button>
              <button type="button" onClick={() => setPendingIncludedOrgIds([])}>
                {t("include_other_pos_clear")}
              </button>
            </div>
          </div>
          <div className="history-other-pos-modal-list">
            {otherOrganizations.map((organization) => (
              <label key={organization.id} className="history-other-pos-modal-option">
                <input
                  type="checkbox"
                  checked={pendingIncludedOrgIds.includes(organization.id)}
                  onChange={() => {
                    setPendingIncludedOrgIds((currentIds) =>
                      currentIds.includes(organization.id)
                        ? currentIds.filter((id) => id !== organization.id)
                        : [...currentIds, organization.id],
                    );
                  }}
                />
                <span>{getOrganizationName(organization)}</span>
              </label>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="themeBtn themeBtnSecondary" onClick={() => setShowOtherPosModal(false)}>
            {t("include_other_pos_cancel")}
          </button>
          <button
            type="button"
            className="themeBtn"
            onClick={() => {
              setIncludedOrgIds(pendingIncludedOrgIds);
              setShowOtherPosModal(false);
            }}
          >
            {t("include_other_pos_confirm")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const Table = ({ orderData, filter, setFilter, handleClickLiquidity, isMultiOrganizationSearch }) => {
  const { t } = useTranslation("history");

  return (
    <div className="card themeCard themeTable br-10 border-transparent ">
      <div className="card-header bg-white border-transparent pt-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 tableHeading">{t("order_history")}</h6>
      </div>

      <CategoryFilter
        filters={[
          { id: "All", label: t("all"), icon: All },
          { id: "Card", label: t("card"), icon: Card },
          { id: "FailedCard", label: t("card_payment_failed"), icon: Card },
          { id: "Cash", label: t("cash"), icon: Cash },
          { id: "Qr", label: t("qr"), icon: Qr },
          { id: "Refund", label: t("refund"), icon: Refund },
          { id: "Free", label: t("free"), icon: Free },
          { id: "Cashless", label: t("cashless"), icon: Cashless },
          { id: "TableNote", label: t("tableNote"), icon: PendingNote },
          { id: "BankTransfer", label: t("bankTransfer"), icon: Invoice },
          { id: "SplitPayments", label: t("splitPayments"), icon: Split },
        ]}
        activeFilter={filter}
        onFilterChange={setFilter}
      />
      <div className="card-header br-10 table-responsive bg-white border-transparent py-3 d-flex flex-row align-items-center justify-content-between">
        <table className="table table-hover">
          <thead>
            <tr>
              <th scope="col">{t("type")}</th>
              {isMultiOrganizationSearch && <th scope="col">{t("pos")}</th>}
              <th scope="col">{t("amount")}</th>
              <th scope="col">{t("timestamp")}</th>
              <th scope="col">{t("cashier")}</th>
              <th scope="col">{t("table")}</th>
              <th scope="col">{t("customer")}</th>
              <th scope="col">{t("invoice_number")}</th>
            </tr>
          </thead>
          <tbody>
            {orderData.filter((order) => matchesOrderFilter(order, filter)).map((val) => (
              <TableRow
                key={val.id}
                val={val}
                isMultiOrganizationSearch={isMultiOrganizationSearch}
                handleClickLiquidity={handleClickLiquidity}
              />
            ))}

            {orderData.length === 0 && (
              <tr>
                <td colSpan={isMultiOrganizationSearch ? 8 : 7}>
                  <h4>{t("no_data")}</h4>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function TableRow({ val, handleClickLiquidity, isMultiOrganizationSearch }) {
  const { t } = useTranslation("history");
  const invoiceData = val?.invoice;
  const isBankTransfer = val?.paymentType === "bankTransfer";
  const invoiceIsPaid = isInvoicePaid(invoiceData);
  const invoiceIcon = isBankTransfer
    ? invoiceIsPaid
      ? Icons.invoice
      : Icons.invoiceNok
    : val?.paymentType === "tableNote"
      ? Icons.receipt
      : val?.paymentType === "cashless"
        ? Icons.cashless
        : val?.paymentType === "free"
          ? Icons.gift
          : val?.paymentType === "card"
            ? Icons.card
            : val?.paymentType === "cash"
              ? Icons.cash
              : val?.paymentType === "splitPayments"
                ? Icons.split
                : Icons.scan;
  const invoiceNumber = invoiceData?.invoiceNumber || val?.invoiceNumber || "-";

  return (
    <tr
      onClick={() => {
        handleClickLiquidity(val);
      }}
    >
      <td scope="row">
        <div className="profileDiv">
          <div className="imgDiv">
            <img src={invoiceIcon} alt="" />
          </div>
          <div className="name">{t(val?.paymentType)}</div>
          {isFailedCardOrder(val) && <span className="ms-2 text-danger">{t("card_payment_failed")}</span>}
        </div>
      </td>
      {isMultiOrganizationSearch && <td>{val?.organizationName || "-"}</td>}
      <td scope="row">
        <div className="profileDiv">
          <div className={val?.totalAmount >= 0 ? "price" : "refund"}>
            {val?.totalAmount.toFixed(2)}
          </div>
        </div>
      </td>

      <td>{val?.createdAt ? moment(parseInt(val.createdAt)).format("DD MMM YY HH:mm:ss") : "-"}</td>

      <td>{val?.createdBy === "cashless" ? "Fast-Pass" : val?.creatorName}</td>
      <td>{val?.tableNumber ? `Table ${val.tableNumber} ${val.tableName}` : "-"}</td>
      <td>
        {val?.customerEmail
          ? val?.customerEmail
          : val?.createdBy === "cashless"
            ? val?.creatorName
            : "-"}
      </td>
      <td>{invoiceNumber}</td>
    </tr>
  );
}

export { History };
